import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, Mail } from "lucide-react";

import { Badge, Button, Select, SearchField, Switch, Table, Tooltip, cx } from "../../../Ui/index.jsx";
import { isJuniorType } from "../../../Shared/ScreenUtils.jsx";
import { AdminTab, timeAgo } from "./adminShared.jsx";

// The People tab shows ONE PAGE of the company's people; search, filters, sort and paging are
// done by the server, so the tab opens as fast for 5,000 people as for 50 (issue 163).

// Calendar and Gmail are two separate connections (an employee can have more than one of either), so the tip
// shows every account found for that person and its own status, not just a single yes/no.
function ConnectionTip({ icon: Icon, connected, label, connections }) {
  const content = (
    <>
      <strong>{label}</strong>
      {connections.length ? (
        <ul className="Adm-ConnList">
          {connections.map((c, i) => (
            <li key={i}><span>{c.email}</span><span>{c.status}</span></li>
          ))}
        </ul>
      ) : <p>Not connected.</p>}
    </>
  );
  return (
    <Tooltip content={content} width={260}>
      <span className={cx(connected ? "ds-text-success" : "ds-muted")} tabIndex={0} role="img" aria-label={`${label}: ${connected ? "connected" : "not connected"}`}>
        <Icon size={16} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}

// "role baseline + extra PAGES" (e.g. 10 +2), both at the page level, so the two numbers are comparable.
// Extra counts every page beyond the role baseline, whatever mechanism granted it.
function PageCount({ user, onAccess }) {
  const total = user.page_count ?? 0;
  const base = user.baseline_page_count ?? total;
  const extra = total - base;
  const title = user.is_superuser
    ? "Superuser — all pages"
    : `${base} from role${extra > 0 ? ` + ${extra} extra` : ""} = ${total} pages`;
  return (
    <Button variant="link" className="ds-code" onClick={() => onAccess(user)} title={title}>
      {user.is_superuser ? "all" : base}
      {!user.is_superuser && extra > 0 && <sup className="ds-text-info">+{extra}</sup>}
      {!user.is_superuser && extra < 0 && <sup className="ds-text-info">&minus;{-extra}</sup>}
    </Button>
  );
}

export function PeopleTab({ people, query, setQuery, busy, act, onAccess, onImpersonate, activeDepartments, isOperator }) {
  const [loginAs, setLoginAs] = useState(false);  // "Login as" pick-a-person mode
  const users = people.users || [];
  const sort = query.sort;
  // Any change to what is asked for starts again from the first page.
  const ask = (change) => setQuery((cur) => ({ ...cur, ...change, page: 1 }));
  const onSort = (k) => ask({ sort: { k, dir: sort.k === k && sort.dir === "asc" ? "desc" : "asc" } });

  // The search box answers a moment after typing stops, not on every key: each answer is a trip
  // to the server, and a company can have thousands of people.
  const [text, setText] = useState(query.q);
  useEffect(() => {
    const typed = text.trim();
    if (typed === query.q) return undefined;
    const wait = setTimeout(() => ask({ q: typed }), 300);
    return () => clearTimeout(wait);
  }, [text]);

  // Department names come from the one shared active-departments list, not from the people
  // themselves — a long-exited employee can still carry a stale/archived department name, and
  // that must not resurrect it as a filter option here. Sorted by headcount, most people first
  // (alphabetical made a department with one person sit above one with thirty).
  const activeDepartmentNames = useMemo(
    () => new Set((activeDepartments || []).map((d) => d.name)),
    [activeDepartments],
  );
  const deptOptions = useMemo(
    () => (people.departments || [])
      .filter((d) => activeDepartmentNames.has(d.name))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .map((d) => d.name),
    [people.departments, activeDepartmentNames],
  );

  const from = people.count ? (people.page - 1) * query.pageSize + 1 : 0;
  const to = Math.min(people.page * query.pageSize, people.count);
  const goToPage = (page) => setQuery((cur) => ({ ...cur, page }));

  const columns = [
    { key: "name", header: "Name", sortable: true, className: "is-card-title", render: (u) => (
      <>
        {loginAs ? (
          <Button variant="link" className="ds-strong" disabled={u.is_superuser} onClick={() => onImpersonate(u)}
            title={u.is_superuser ? "Can't log in as a superuser" : `Login as ${u.name}`}>{u.name}</Button>
        ) : <span className="ds-strong">{u.name}</span>}
        <span className="ds-small ds-code Adm-Line">{u.username}</span>
      </>
    ) },
    { key: "department", header: "Department", sortable: true, render: (u) => u.department || "—" },
    { key: "family", header: "Family", sortable: true, render: (u) => (u.family ? <Badge variant="info">{u.family_label || u.family}</Badge> : "—") },
    { key: "employment_type", header: "Type", sortable: true, render: (u) => (
      u.employment_type ? <Badge variant={isJuniorType(u.employment_type) ? undefined : "success"}>{u.employment_type}</Badge> : "—"
    ) },
    { key: "gmail", header: "Gmail", align: "center", render: (u) => <ConnectionTip icon={Mail} connected={u.gmail_connected} label="Gmail" connections={u.gmail_connections || []} /> },
    { key: "calendar", header: "Calendar", align: "center", render: (u) => <ConnectionTip icon={CalendarDays} connected={u.calendar_connected} label="Google Calendar" connections={u.calendar_connections || []} /> },
    { key: "page_count", header: "Pages", sortable: true, align: "right", render: (u) => <PageCount user={u} onAccess={onAccess} /> },
    { key: "is_active", header: "Active", sortable: true, className: "is-card-amount", render: (u) => (
      <span className="Adm-Active">
        <Switch on={u.is_active} label={`${u.name} is active`} onChange={(next) => act(u.id, { action: "set_active", value: next })} />
        {u.is_active && u.hr_status === "Exited" && (
          <Badge variant="warning" title="HR marked this person Exited, but their login was never deactivated."><AlertTriangle size={12} aria-hidden="true" /> Exited</Badge>
        )}
      </span>
    ) },
    { key: "last_login", header: "Last seen", sortable: true, align: "right", className: "ds-small", render: (u) => timeAgo(u.last_login) },
  ];

  return (
    <AdminTab title="People" subtitle={`${people.count} in total`}>
      <div className="ds-toolbar">
        <SearchField className="Adm-Search" value={text} onChange={setText} placeholder="Search people" />
        <Select aria-label="Status" value={query.status} onChange={(e) => ask({ status: e.target.value })}
          options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Deactivated" }, { value: "all", label: "All" }]} />
        <Select aria-label="Type" value={query.type} onChange={(e) => ask({ type: e.target.value })}
          options={[{ value: "all", label: "All types" }, { value: "ft", label: "Full-time" }, { value: "intern", label: "Juniors" }]} />
        <Select aria-label="Department" value={query.department} onChange={(e) => ask({ department: e.target.value })} title="Filter by department"
          options={[{ value: "all", label: "All departments" }, ...deptOptions]} />
        {isOperator && (
          <Button className="Adm-End" aria-pressed={loginAs} onClick={() => setLoginAs((v) => !v)} title="View the app exactly as another person sees it">
            {loginAs ? "Pick a person… (cancel)" : "Login as…"}
          </Button>
        )}
      </div>
      {loginAs && (
        <p className="ds-text-info">
          Click any person below to view the whole app as them — their nav, access, and masked contact details. You can exit from the top bar afterwards.
        </p>
      )}
      <div className={cx(busy && users.length > 0 && "Adm-Busy")}>
        <Table stack columns={columns} rows={users} loading={busy} empty="No people match."
          sort={{ key: sort.k, dir: sort.dir }} onSort={onSort}
          rowClassName={(u) => (u.is_active ? "" : "is-dim")} />
      </div>
      {people.count > 0 && (
        <div className="ds-toolbar Adm-Pager">
          <span className="ds-small">{from}–{to} of {people.count}</span>
          <Button size="sm" disabled={busy || people.page <= 1} onClick={() => goToPage(people.page - 1)}>Previous</Button>
          <span className="ds-small">Page {people.page} of {people.pages}</span>
          <Button size="sm" disabled={busy || people.page >= people.pages} onClick={() => goToPage(people.page + 1)}>Next</Button>
        </div>
      )}
    </AdminTab>
  );
}
